import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { BarChart3 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip } from "recharts";
import { getSeverityColorValue } from "./utils";

interface CVSSDistributionCardProps {
  vulnerabilities: Array<{ cvss_score: number; severity: string }>;
}

export function CVSSDistributionCard({ vulnerabilities }: CVSSDistributionCardProps) {
  // Process CVSS scores into ranges
  const cvssRanges = [
    { range: "0-1", min: 0, max: 1, color: 'hsl(142 76% 36%)' },
    { range: "1-2", min: 1, max: 2, color: 'hsl(142 76% 36%)' },
    { range: "2-3", min: 2, max: 3, color: 'hsl(142 76% 36%)' },
    { range: "3-4", min: 3, max: 4, color: 'hsl(142 76% 36%)' },
    { range: "4-5", min: 4, max: 5, color: 'hsl(45 90% 55%)' },
    { range: "5-6", min: 5, max: 6, color: 'hsl(45 90% 55%)' },
    { range: "6-7", min: 6, max: 7, color: 'hsl(45 90% 55%)' },
    { range: "7-8", min: 7, max: 8, color: 'hsl(30 80% 55%)' },
    { range: "8-9", min: 8, max: 9, color: 'hsl(30 80% 55%)' },
    { range: "9-10", min: 9, max: 10, color: 'hsl(0 84.2% 60.2%)' },
  ];

  const distributionData = cvssRanges.map(rangeItem => {
    const count = vulnerabilities.filter(
      v => v.cvss_score >= rangeItem.min && v.cvss_score < rangeItem.max + 0.1
    ).length;
    return {
      ...rangeItem,
      count,
    };
  });

  // Calculate weighted average CVSS score
  const weightedAverage = vulnerabilities.length > 0
    ? vulnerabilities.reduce((sum, v) => sum + v.cvss_score, 0) / vulnerabilities.length
    : 0;

  // Get severity from weighted average
  const getAverageSeverity = (score: number) => {
    if (score >= 9.0) return 'CRITICAL';
    if (score >= 7.0) return 'HIGH';
    if (score >= 4.0) return 'MEDIUM';
    return 'LOW';
  };

  const averageSeverity = getAverageSeverity(weightedAverage);

  return (
    <GlassCard variant="default" className="group">
      <GlassCardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <GlassCardTitle className="text-sm font-medium">CVSS Score Distribution</GlassCardTitle>
        <div className="p-2 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 backdrop-blur-sm border border-primary/20 group-hover:from-primary/20 group-hover:to-primary/10 transition-all duration-300">
          <BarChart3 className="h-4 w-4 text-primary" />
        </div>
      </GlassCardHeader>
      <GlassCardContent>
        <div className="h-[280px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={distributionData} margin={{ top: 10, right: 10, left: -20, bottom: 5 }}>
              <XAxis 
                dataKey="range" 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis 
                tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'hsl(var(--popover))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ color: 'hsl(var(--popover-foreground))' }}
                cursor={{ fill: 'hsl(var(--muted) / 0.3)' }}
              />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {distributionData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Weighted Average CVSS Score</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold">{weightedAverage.toFixed(1)}</span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div 
              className="px-3 py-1 rounded-full text-xs font-semibold"
              style={{ 
                backgroundColor: `${getSeverityColorValue(averageSeverity)}20`,
                color: getSeverityColorValue(averageSeverity)
              }}
            >
              {averageSeverity}
            </div>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}
